import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FaEdit,
  FaPlus,
  FaRedo,
  FaSearch,
  FaTrash,
  FaUserCheck,
  FaUserCog,
  FaUsers,
} from "react-icons/fa";
import { RiMailCheckFill } from "react-icons/ri";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import useScrollToTop from "../../../../hooks/useScollToTop";
import { useUser } from "../../../../pages/Home/UserContext";
import { getUserRoleId } from "../../../config/dashboardMenuConfig";

import {
  getRoles,
  getAllUsers,
  getLatestUsers,
  deleteUser as deleteUserAPI,
  updateUserRole,
  toggleReceiveUserMessages,
} from "../../../../services/userServices";

import { sendNotification } from "../../../../services/notificationServices";
import { toAbsoluteUserPhotoUrl } from "../../../../services/userPhotoServices";

import AddUserDrawer from "./components/AddUserDrawer";
import EditUserDrawer from "./components/EditUserDrawer";
import ManageAdherentsDrawer from "./components/ManageAdherentsDrawer";

const CHUNK = 60;
const ROLE_UPGRADE_IDS = new Set([1, 3]);

function getStoredUserInfo() {
  try {
    const raw = localStorage.getItem("userInfo");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function extractArray(data) {
  if (Array.isArray(data)) return data;

  if (data && typeof data === "object") {
    if (Array.isArray(data.rows)) return data.rows;
    if (Array.isArray(data.items)) return data.items;
    if (Array.isArray(data.users)) return data.users;
    if (Array.isArray(data.data)) return data.data;
  }

  return [];
}

function getUserId(user) {
  return user?.id ?? user?.user_id ?? user?.userId;
}

function getCreatedDate(user) {
  return user?.createdOn || user?.created_at || user?.createdAt || null;
}

function formatDate(dateValue) {
  if (!dateValue) return "—";

  const date = new Date(dateValue);

  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function sortIcon(order) {
  if (order === "asc") return "▲";
  if (order === "desc") return "▼";
  return "⇅";
}

function getRoleDisplay(roleId) {
  switch (Number(roleId)) {
    case 1:
      return {
        name: "Administrateur",
        className:
          "bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/20",
      };
    case 2:
      return {
        name: "Utilisateur",
        className:
          "bg-indigo-50 text-indigo-700 ring-indigo-200 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-500/20",
      };
    case 3:
      return {
        name: "Enseignant",
        className:
          "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20",
      };
    default:
      return {
        name: "Rôle inconnu",
        className:
          "bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700",
      };
  }
}

function countByRole(users, roleId) {
  return users.filter((user) => Number(user?.role_id) === Number(roleId))
    .length;
}

function MiniStat({ icon, label, value, helper, accent = "indigo" }) {
  const accents = {
    indigo:
      "border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300",
    emerald:
      "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300",
    amber:
      "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300",
    rose: "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300",
  };

  return (
    <div className={`rounded-2xl border px-4 py-3 ${accents[accent]}`}>
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/80 dark:bg-slate-900/50">
          {icon}
        </div>

        <div className="min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-[0.16em] opacity-75">
            {label}
          </p>

          <p className="mt-0.5 text-xl font-bold text-slate-950 dark:text-white">
            {Number(value || 0).toLocaleString("fr-FR")}
          </p>

          {helper ? (
            <p className="mt-0.5 text-xs text-slate-600 dark:text-slate-300">
              {helper}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function UnauthorizedDashboardPanel() {
  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1900px] px-3 sm:px-4 lg:px-6">
        <div className="overflow-hidden rounded-[28px] border border-rose-200 bg-white shadow-sm dark:border-rose-500/20 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-rose-500 to-orange-500" />

          <div className="p-5 sm:p-6">
            <p className="text-[11px] uppercase tracking-[0.22em] text-rose-500">
              Accès refusé
            </p>

            <h1 className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
              Listing utilisateurs
            </h1>

            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              Cette page est réservée aux administrateurs du club.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function UserListing() {
  useScrollToTop();

  const { user } = useUser();
  const storedUser = useMemo(() => getStoredUserInfo(), []);
  const roleId = getUserRoleId(user || storedUser);

  const scrollRef = useRef(null);

  const [users, setUsers] = useState([]);
  const [latestUsers, setLatestUsers] = useState([]);
  const [roles, setRoles] = useState({});

  const [viewMode, setViewMode] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [createdSortOrder, setCreatedSortOrder] = useState("desc");
  const [visibleCount, setVisibleCount] = useState(CHUNK);

  const [loading, setLoading] = useState(true);
  const [editingRoleUserId, setEditingRoleUserId] = useState(null);

  const [drawer, setDrawer] = useState({
    type: null,
    userId: null,
  });

  const activeSource = viewMode === "latest" ? latestUsers : users;

  const stats = useMemo(() => {
    return {
      total: users.length,
      utilisateurs: countByRole(users, 2),
      enseignants: countByRole(users, 3),
      admins: countByRole(users, 1),
    };
  }, [users]);

  const rows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    const filtered = activeSource.filter((currentUser) => {
      const fullName =
        `${currentUser?.nom || ""} ${currentUser?.prenom || ""}`.toLowerCase();
      const email = String(currentUser?.email || "").toLowerCase();

      const matchesSearch =
        !term || fullName.includes(term) || email.includes(term);

      const matchesRole =
        selectedRole === "" ||
        String(currentUser?.role_id) === String(selectedRole);

      return matchesSearch && matchesRole;
    });

    const sorted = [...filtered];

    if (createdSortOrder !== "none") {
      sorted.sort((a, b) => {
        const da = new Date(getCreatedDate(a) || 0).getTime();
        const db = new Date(getCreatedDate(b) || 0).getTime();

        return createdSortOrder === "asc" ? da - db : db - da;
      });
    }

    return sorted;
  }, [activeSource, searchTerm, selectedRole, createdSortOrder]);

  const visibleRows = rows.slice(0, visibleCount);

  const resetScroll = () => {
    setVisibleCount(CHUNK);
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  };

  const fetchData = async () => {
    try {
      setLoading(true);

      const [usersResponse, rolesResponse] = await Promise.all([
        getAllUsers(),
        getRoles(),
      ]);

      const usersList = extractArray(usersResponse);
      const rolesList = extractArray(rolesResponse);

      const rolesMap = rolesList.reduce((acc, currentRole) => {
        acc[currentRole.id] = currentRole.nom;
        return acc;
      }, {});

      setUsers(usersList);
      setRoles(rolesMap);
      setLatestUsers([]);
      setViewMode("all");
      resetScroll();
    } catch (error) {
      console.error("Erreur chargement utilisateurs :", error);
      toast.error("Erreur lors du chargement des utilisateurs.");
    } finally {
      setLoading(false);
    }
  };

  const fetchLatest = async () => {
    try {
      setLoading(true);

      const response = await getLatestUsers();
      setLatestUsers(extractArray(response));
      setViewMode("latest");
      resetScroll();
    } catch (error) {
      console.error("Erreur derniers utilisateurs :", error);
      toast.error("Erreur lors de la récupération des derniers utilisateurs.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const updateUserInState = (userId, patch) => {
    const updater = (list) =>
      list.map((item) =>
        String(getUserId(item)) === String(userId)
          ? { ...item, ...patch }
          : item,
      );

    setUsers(updater);
    setLatestUsers(updater);
  };

  const removeUserFromState = (userId) => {
    const filterer = (list) =>
      list.filter((item) => String(getUserId(item)) !== String(userId));

    setUsers(filterer);
    setLatestUsers(filterer);
  };

  const sendRoleUpgradeNotification = async (recipientId, token) => {
    const message =
      "L'administrateur vous a changé de rôle sur l'application. Vous avez désormais accès aux pages Enseignant ou Administrateur. Si vous ne voyez pas ces nouvelles pages, déconnectez-vous puis reconnectez-vous.";

    await sendNotification(message, recipientId, token);
  };

  const handleRoleChange = async (userId, newRoleId) => {
    try {
      const token =
        user?.token ||
        user?.accessToken ||
        localStorage.getItem("token") ||
        sessionStorage.getItem("token");

      const nextRoleId = Number(newRoleId);
      const currentUser = users.find(
        (item) => String(getUserId(item)) === String(userId),
      );
      const prevRoleId = Number(currentUser?.role_id);

      await updateUserRole(userId, nextRoleId, token);

      updateUserInState(userId, { role_id: nextRoleId });
      setEditingRoleUserId(null);

      if (
        ROLE_UPGRADE_IDS.has(nextRoleId) &&
        !ROLE_UPGRADE_IDS.has(prevRoleId) &&
        prevRoleId !== nextRoleId
      ) {
        try {
          await sendRoleUpgradeNotification(userId, token);
          toast.success("Rôle mis à jour et notification envoyée.");
        } catch (notificationError) {
          console.error(
            "Erreur notification changement rôle :",
            notificationError,
          );
          toast.success("Rôle mis à jour.");
          toast.warn("La notification n'a pas pu être envoyée.");
        }
      } else {
        toast.success("Rôle mis à jour.");
      }
    } catch (error) {
      console.error("Erreur mise à jour rôle :", error);
      toast.error("Erreur lors de la mise à jour du rôle.");
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm(`Supprimer cet utilisateur ? ID : ${userId}`)) return;

    try {
      const message = await deleteUserAPI(userId);
      removeUserFromState(userId);
      toast.success(message || "Utilisateur supprimé.");
    } catch (error) {
      console.error("Erreur suppression utilisateur :", error);
      toast.error("Erreur lors de la suppression de l'utilisateur.");
    }
  };

  const handleToggleReceiveMessages = async (userId, currentValue) => {
    try {
      const token =
        user?.token ||
        user?.accessToken ||
        localStorage.getItem("token") ||
        sessionStorage.getItem("token");

      const updated = await toggleReceiveUserMessages(
        userId,
        !currentValue,
        token,
      );

      const newValue =
        typeof updated?.receive_messages !== "undefined"
          ? Boolean(updated.receive_messages)
          : !currentValue;

      updateUserInState(userId, { receive_messages: newValue });

      toast.success(
        newValue
          ? "Réception des messages activée."
          : "Réception des messages désactivée.",
      );
    } catch (error) {
      console.error("Erreur réception messages :", error);
      toast.error("Erreur lors du changement de réception des messages.");
    }
  };

  const handleTableScroll = (event) => {
    const element = event.currentTarget;

    if (
      element.scrollTop + element.clientHeight >=
      element.scrollHeight - 220
    ) {
      setVisibleCount((current) =>
        current >= rows.length
          ? current
          : Math.min(current + CHUNK, rows.length),
      );
    }
  };

  const toggleCreatedSort = () => {
    setCreatedSortOrder((current) => {
      if (current === "none") return "asc";
      if (current === "asc") return "desc";
      return "none";
    });
  };

  if (roleId !== 1) {
    return <UnauthorizedDashboardPanel />;
  }

  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1900px] space-y-5 px-3 sm:px-4 lg:px-6">
        <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

          <div className="p-4 sm:p-5 lg:p-6">
            <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                  Administration
                </p>

                <h1 className="mt-1 text-2xl font-bold text-slate-900 sm:text-[30px] dark:text-white">
                  Listing utilisateurs
                </h1>

                <p className="mt-2 max-w-4xl text-sm leading-6 text-slate-600 dark:text-slate-300">
                  Gérez les comptes, les rôles, les accès aux messages et les
                  adhérents liés.
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setDrawer({ type: "add", userId: null })}
                  className="inline-flex items-center gap-2 rounded-2xl bg-indigo-600 px-4 py-2 text-sm font-bold text-white shadow-sm transition hover:bg-indigo-700"
                >
                  <FaPlus />
                  Ajouter
                </button>

                <button
                  type="button"
                  onClick={fetchLatest}
                  className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  Derniers inscrits
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setViewMode("all");
                    resetScroll();
                  }}
                  className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  Tous
                </button>

                <button
                  type="button"
                  onClick={fetchData}
                  disabled={loading}
                  className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  <FaRedo className={loading ? "animate-spin" : ""} />
                  Actualiser
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <MiniStat
            icon={<FaUsers />}
            label="Comptes"
            value={stats.total}
            helper="Total utilisateurs"
            accent="indigo"
          />
          <MiniStat
            icon={<FaUserCheck />}
            label="Utilisateurs"
            value={stats.utilisateurs}
            helper="Rôle Utilisateur"
            accent="emerald"
          />
          <MiniStat
            icon={<FaUserCog />}
            label="Enseignants"
            value={stats.enseignants}
            helper="Rôle Enseignant"
            accent="amber"
          />
          <MiniStat
            icon={<FaUserCog />}
            label="Admins"
            value={stats.admins}
            helper="Rôle Administrateur"
            accent="rose"
          />
        </div>

        <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="border-b border-slate-200 p-4 dark:border-slate-800 sm:p-5 lg:p-6">
            <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
              <div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                  Tableau des utilisateurs
                </h2>

                <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-slate-300">
                  {rows.length.toLocaleString("fr-FR")} utilisateur
                  {rows.length > 1 ? "s" : ""} affiché
                  {rows.length > 1 ? "s" : ""}
                  {viewMode === "latest" ? " · vue derniers inscrits" : ""}
                </p>
              </div>

              <div className="grid gap-2 sm:grid-cols-[minmax(240px,1fr)_220px] xl:min-w-[620px]">
                <div className="relative">
                  <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(event) => {
                      setSearchTerm(event.target.value);
                      resetScroll();
                    }}
                    placeholder="Rechercher nom, prénom ou email..."
                    className="w-full rounded-2xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                  />
                </div>

                <select
                  value={selectedRole}
                  onChange={(event) => {
                    setSelectedRole(event.target.value);
                    resetScroll();
                  }}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                >
                  <option value="">Tous les rôles</option>
                  {Object.entries(roles).map(([id, name]) => (
                    <option key={id} value={id}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-4 rounded-2xl border border-indigo-200 bg-indigo-50 px-4 py-3 text-xs leading-5 text-indigo-800 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300">
              Cliquez sur un badge de rôle pour le modifier. Le bouton “+/−”
              permet de lier un ou plusieurs adhérents au compte. L’enveloppe
              active ou désactive la réception des messages pour les enseignants
              et administrateurs.
            </div>
          </div>

          <div className="p-3 sm:p-5 lg:p-6">
            <div
              ref={scrollRef}
              onScroll={handleTableScroll}
              className="max-h-[72vh] overflow-auto rounded-[24px] border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950/60"
            >
              <table className="min-w-[1180px] w-full text-sm">
                <thead className="sticky top-0 z-30 bg-slate-50 dark:bg-slate-900">
                  <tr className="text-left">
                    <th className="sticky left-0 z-40 bg-slate-50 px-4 py-3 font-bold text-slate-700 dark:bg-slate-900 dark:text-slate-200">
                      Photo
                    </th>

                    <th className="sticky left-[88px] z-40 bg-slate-50 px-4 py-3 font-bold text-slate-700 dark:bg-slate-900 dark:text-slate-200">
                      Nom prénom
                    </th>

                    <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                      Email
                    </th>

                    <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                      Rôle
                    </th>

                    <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                      <button
                        type="button"
                        onClick={toggleCreatedSort}
                        className="inline-flex items-center gap-2 rounded-xl px-2 py-1 transition hover:bg-slate-100 dark:hover:bg-slate-800"
                      >
                        Créé le
                        <span>{sortIcon(createdSortOrder)}</span>
                      </button>
                    </th>

                    <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                      Adhérents liés
                    </th>

                    <th className="px-4 py-3 text-right font-bold text-slate-700 dark:text-slate-200">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {loading ? (
                    [...Array(8)].map((_, index) => (
                      <tr key={index}>
                        <td colSpan={7} className="px-4 py-3">
                          <div className="h-8 animate-pulse rounded-xl bg-slate-200 dark:bg-slate-800" />
                        </td>
                      </tr>
                    ))
                  ) : visibleRows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={7}
                        className="px-4 py-10 text-center text-slate-500 dark:text-slate-400"
                      >
                        Aucun utilisateur ne correspond aux filtres.
                      </td>
                    </tr>
                  ) : (
                    visibleRows.map((currentUser) => {
                      const currentUserId = getUserId(currentUser);
                      const role = getRoleDisplay(currentUser.role_id);
                      const isEditingRole =
                        String(editingRoleUserId) === String(currentUserId);

                      return (
                        <tr
                          key={currentUserId}
                          className="transition hover:bg-slate-50 dark:hover:bg-slate-900"
                        >
                          <td className="sticky left-0 z-20 bg-white px-4 py-3 dark:bg-slate-950">
                            <img
                              src={toAbsoluteUserPhotoUrl(
                                currentUser.photoURL || "/img/user_avatar.jpg",
                              )}
                              alt={`${currentUser.nom || ""} ${currentUser.prenom || ""}`}
                              className="h-11 w-11 rounded-2xl object-cover ring-1 ring-slate-200 dark:ring-slate-700"
                              onError={(event) => {
                                event.currentTarget.src =
                                  "/img/user_avatar.jpg";
                              }}
                            />
                          </td>

                          <td className="sticky left-[88px] z-20 bg-white px-4 py-3 dark:bg-slate-950">
                            <div className="font-bold text-slate-900 dark:text-white">
                              {currentUser.nom || "—"}{" "}
                              {currentUser.prenom || ""}
                            </div>
                            <div className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                              ID {currentUserId}
                            </div>
                          </td>

                          <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                            {currentUser.email || "—"}
                          </td>

                          <td className="px-4 py-3">
                            {isEditingRole ? (
                              <select
                                value={currentUser.role_id}
                                onChange={(event) =>
                                  handleRoleChange(
                                    currentUserId,
                                    event.target.value,
                                  )
                                }
                                onBlur={() => setEditingRoleUserId(null)}
                                autoFocus
                                className="rounded-2xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                              >
                                {Object.entries(roles).map(([id, name]) => (
                                  <option key={id} value={id}>
                                    {name}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <button
                                type="button"
                                onClick={() =>
                                  setEditingRoleUserId(currentUserId)
                                }
                                className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ring-1 ${role.className}`}
                                title="Cliquer pour modifier le rôle"
                              >
                                {role.name}
                              </button>
                            )}
                          </td>

                          <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                            {formatDate(getCreatedDate(currentUser))}
                          </td>

                          <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                            {Array.isArray(currentUser.adherents) &&
                            currentUser.adherents.length > 0 ? (
                              <div className="max-w-[240px] space-y-1">
                                {currentUser.adherents
                                  .slice(0, 3)
                                  .map((adherent, index) => (
                                    <div
                                      key={`${currentUserId}-${adherent.id || index}`}
                                      className="truncate rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                                    >
                                      {adherent.nom} {adherent.prenom}
                                    </div>
                                  ))}

                                {currentUser.adherents.length > 3 ? (
                                  <div className="text-xs text-slate-500">
                                    +{currentUser.adherents.length - 3} autre(s)
                                  </div>
                                ) : null}
                              </div>
                            ) : (
                              <span className="text-slate-400">Aucun</span>
                            )}
                          </td>

                          <td className="px-4 py-3">
                            <div className="flex justify-end gap-2">
                              <button
                                type="button"
                                onClick={() =>
                                  setDrawer({
                                    type: "edit",
                                    userId: currentUserId,
                                  })
                                }
                                className="flex h-9 w-9 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                                title="Modifier"
                              >
                                <FaEdit />
                              </button>

                              {[1, 3].includes(Number(currentUser.role_id)) ? (
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleToggleReceiveMessages(
                                      currentUserId,
                                      Boolean(currentUser.receive_messages),
                                    )
                                  }
                                  className="flex h-9 w-9 items-center justify-center rounded-2xl border border-slate-200 bg-white transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:hover:bg-slate-800"
                                  title={
                                    currentUser.receive_messages
                                      ? "Désactiver la réception des messages"
                                      : "Activer la réception des messages"
                                  }
                                >
                                  <RiMailCheckFill
                                    className={
                                      currentUser.receive_messages
                                        ? "text-emerald-500"
                                        : "text-slate-400"
                                    }
                                    size={18}
                                  />
                                </button>
                              ) : null}

                              <button
                                type="button"
                                onClick={() =>
                                  setDrawer({
                                    type: "adherents",
                                    userId: currentUserId,
                                  })
                                }
                                className="flex h-9 min-w-9 items-center justify-center rounded-2xl bg-emerald-600 px-2 text-xs font-bold text-white transition hover:bg-emerald-700"
                                title="Gérer les adhérents liés"
                              >
                                +/−
                              </button>

                              <button
                                type="button"
                                onClick={() => handleDeleteUser(currentUserId)}
                                className="flex h-9 w-9 items-center justify-center rounded-2xl bg-rose-600 text-white transition hover:bg-rose-700"
                                title="Supprimer"
                              >
                                <FaTrash />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>

              {!loading && visibleCount < rows.length ? (
                <div className="border-t border-slate-200 p-3 text-center text-xs text-slate-500 dark:border-slate-800 dark:text-slate-400">
                  Affichage de {visibleCount} / {rows.length} — descendez pour
                  charger la suite.
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <AddUserDrawer
        isOpen={drawer.type === "add"}
        onClose={() => setDrawer({ type: null, userId: null })}
        roles={roles}
        refreshList={fetchData}
      />

      <EditUserDrawer
        isOpen={drawer.type === "edit"}
        onClose={() => setDrawer({ type: null, userId: null })}
        userId={drawer.userId}
        refreshList={fetchData}
      />

      <ManageAdherentsDrawer
        isOpen={drawer.type === "adherents"}
        onClose={() => setDrawer({ type: null, userId: null })}
        userId={drawer.userId}
        refresh={fetchData}
      />

      <ToastContainer />
    </section>
  );
}
